import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Creator } from '../types';
import { InstagramIcon, BackArrowIcon } from '../components/Icons';

interface CreatorProfileProps {
    creators: Creator[];
}

const CreatorProfile: React.FC<CreatorProfileProps> = ({ creators }) => {
    const { creatorId } = useParams<{ creatorId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [activeTabKey, setActiveTabKey] = useState<string>('creatorProfile.tabContent');

    const creator = creators.find(c => c.id === creatorId);

    if (!creator) {
        return <div className="p-4 text-center text-gray-600 dark:text-gray-300">{t('creatorProfile.creatorNotFound')}</div>;
    }

    const tabs = [
        { key: 'creatorProfile.tabContent', label: t('creatorProfile.tabContent') },
        { key: 'creatorProfile.tabStats', label: t('creatorProfile.tabStats'), link: `/analytics/${creator.id}` },
        { key: 'creatorProfile.tabNotes', label: t('creatorProfile.tabNotes') },
    ];

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-start gap-4 mb-6">
                 <button onClick={() => navigate(-1)} className="mt-1 flex-shrink-0 size-8 items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 sm:size-10">
                        <BackArrowIcon />
                 </button>
                <div className="flex flex-col items-start sm:flex-row sm:items-center sm:gap-6 flex-grow">
                    <img alt={`${creator.name} Profile Picture`} className="h-28 w-28 rounded-full object-cover" src={creator.avatarUrl} />
                    <div className='mt-4 sm:mt-0'>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">{creator.name}</h1>
                        <p className="text-gray-500 dark:text-gray-400">{creator.handle}</p>
                        <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-blue-100 dark:bg-blue-900/50 px-3 py-1 text-sm font-medium text-blue-800 dark:text-blue-300">
                           <InstagramIcon />
                           {t(`platform.${creator.platform}`)}
                        </div>
                    </div>
                </div>
            </div>

            <div className="border-b border-gray-200 dark:border-gray-700">
                <nav aria-label="Tabs" className="-mb-px flex justify-start space-x-8">
                    {tabs.map(tab => {
                        const content = (
                            <span className={`whitespace-nowrap py-4 px-1 text-sm font-medium ${activeTabKey === tab.key ? 'text-[var(--primary-color)] border-b-2 border-[var(--primary-color)]' : 'text-gray-500 dark:text-gray-400 hover:text-[var(--primary-color)] hover:border-b-2 hover:border-[var(--primary-color)]'}`}>
                                {tab.label}
                            </span>
                        );
                        
                        if (tab.link) {
                            return <Link to={tab.link} key={tab.key}>{content}</Link>;
                        }

                        return (
                            <button key={tab.key} onClick={() => setActiveTabKey(tab.key)}>
                                {content}
                            </button>
                        );
                    })}
                </nav>
            </div>

            <div className="mt-6">
                {activeTabKey === 'creatorProfile.tabContent' && (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                        {creator.videos.map(video => (
                            <div key={video.id} className="aspect-w-1 aspect-h-1 group relative overflow-hidden rounded-lg">
                                <img alt={`Content by ${creator.name}`} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" src={video.thumbnailUrl} />
                                <div className="absolute inset-0 bg-black/20"></div>
                            </div>
                        ))}
                    </div>
                )}
                {activeTabKey === 'creatorProfile.tabNotes' && (
                    <div className="text-center text-gray-500 dark:text-gray-400 py-10 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                        <p>{t('creatorProfile.noNotes')}</p>
                        <button className="mt-4 px-4 py-2 bg-[var(--primary-color)] text-white rounded-lg hover:bg-opacity-90">{t('creatorProfile.addNote')}</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CreatorProfile;